// eslint-disable-next-line no-restricted-imports
import { ResizeObserver } from '@juggle/resize-observer';
import classNames from 'classnames';
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import Breadcrumb from './Breadcrumb';
import styles from './Breadcrumbs.module.scss';

export type BreadcrumbItem = { name: React.ReactNode; to?: string };

export interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  size?: 'small' | 'regular';
  truncate?: boolean;
}

const Breadcrumbs = ({
  items,
  size = 'regular',
  truncate = false,
}: BreadcrumbsProps) => {
  const ref = useRef<HTMLOListElement>(null);
  const [isOverflowed, setIsOverflowed] = useState(false);
  const updateIsOverflowed = useCallback(() => {
    if (truncate) {
      setIsOverflowed(
        ref.current ? ref.current.clientWidth < ref.current.scrollWidth : false,
      );
    }
  }, [truncate]);

  useLayoutEffect(updateIsOverflowed);

  useLayoutEffect(() => {
    if (truncate && ref.current) {
      const resizeObserver = new ResizeObserver(updateIsOverflowed);
      resizeObserver.observe(ref.current);
      return () => resizeObserver.disconnect();
    }
    return undefined;
  }, [truncate, updateIsOverflowed]);

  return (
    <ol
      ref={ref}
      className={classNames(styles.container, {
        [styles.small]: size === 'small',
        [styles.truncate]: truncate,
        [styles.overflow]: isOverflowed,
      })}
    >
      {items.map((item, index) => (
        <Breadcrumb
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          truncate={truncate}
          priority={(() => {
            if (!truncate) return undefined;
            if (index === items.length - 1) return 'max';
            if (index === 0) return 'high';
            return 'low';
          })()}
          {...item}
        />
      ))}
    </ol>
  );
};

export default Breadcrumbs;
